import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { handleSnackBar } from "./global";
import { postAPICallServer } from "../utils/axios.util";
import { apiServerRoutes } from "../constants/apiRoutes";
import authService from "../services/auth.service";
import MESSAGES from "../constants/messages";
import { DEVICE_TYPE, REGISTRATION_TYPES } from "../constants/general";
import { getAuthUserId } from "../utils/auth.util";
import { prepareNotificationToken } from "../firebase-config/firebase";
import { removeAccessToken, setAccessToken } from "../utils/cookies";
import userService from "../services/user.service";
import { getUserProfile } from "./userReducer";

interface loginStateInterface {
  displayName: string;
  email: string;
  pic: string;
}

export const setIsLoggedIn = createAsyncThunk(
  "auth/setIsLoggedIn",
  (data: boolean) => {
    return data;
  }
);

export const setIsLoginData = createAsyncThunk(
  "auth/setIsLoginData",
  (data: loginStateInterface) => {
    return data;
  }
);

export const setIsLoadingAuth = createAsyncThunk(
  "auth/setIsLoadingAuth",
  (data: boolean) => {
    return data;
  }
);

export const signUpUserAction = createAsyncThunk(
  "auth/signupUser",
  async ({ data, navigate }: any, { dispatch, getState }: any) => {
    try {
      dispatch(setSignUpFormError(""));
      dispatch(setIsSubmitting(true));
      console.log("checking availabilty", getState()?.authReducer);
      // check email and phone availibity into database
      const checkAvailibilty = await userService.checkAvailibilty({
        body: { email: data?.email, mobile_number: data?.phone },
      });
      console.log("checked availabilty", getState()?.authReducer);
      if (checkAvailibilty.data.data.message !== "AVAILABLE") {
        dispatch(
          setSignUpFormError(
            checkAvailibilty.data.data.message === "EMAIL_EXISTS"
              ? MESSAGES.DUPLICATE_EMAIL
              : MESSAGES.DUPLICATE_PHONE
          )
        );

        if (data.registration_type !== REGISTRATION_TYPES.EMAIL) {
          dispatch(
            handleSnackBar({
              open: true,
              severity: "error",
              message:
                checkAvailibilty.data.data.message === "EMAIL_EXISTS"
                  ? MESSAGES.DUPLICATE_EMAIL
                  : MESSAGES.DUPLICATE_PHONE,
            })
          );
        }
        console.log("Doing Logout", getState()?.authReducer);
        await authService.doLogout();
        console.log("Post Logout", getState()?.authReducer);
        dispatch(setIsSubmitting(false));
        return;
      } else {
        let payload = data;
        if (data.registration_type === REGISTRATION_TYPES.EMAIL) {
          console.log("Initiate Auth SIgnup", getState()?.authReducer);
          payload = await authService.signUpUser(data);
          console.log("Auth  SIgnup done", getState()?.authReducer);
          payload.registration_type = REGISTRATION_TYPES.EMAIL;
        }

        payload.device_type = DEVICE_TYPE; // for WEB
        // call sign up when email and phone is available
        console.log("Called SIgnup", getState()?.authReducer);
        const postCall = await postAPICallServer(
          apiServerRoutes.SIGNUP,
          payload
        );
        console.log("Completed SIgnup", getState()?.authReducer);
        dispatch(setIsSubmitting(false));
        if (data.registration_type === REGISTRATION_TYPES.EMAIL) {
          dispatch(setOtpVerification(true));
          dispatch(setOtpTokenData(postCall.data.data.otp));
        } else {
          // Call API to get and set auth token
          console.log("Fetch Tokens");
          const FCMToken = await prepareNotificationToken();
          const tokenRes = await authService.getAuthToken(
            data.firebase_id,
            FCMToken
          );
          console.log("Fetched Tokens", getState()?.authReducer);
          setAccessToken(tokenRes.data.data.token);
          console.log(" Token setted", getState()?.authReducer);
          dispatch(getUserProfile());
          navigate();
        }
        return postCall.data;
      }
    } catch (e: any) {
      if (e.code === "auth/email-already-in-use") {
        dispatch(setSignUpFormError(MESSAGES.DUPLICATE_EMAIL));

        dispatch(setIsSubmitting(false));
        return;
      }

      dispatch(
        handleSnackBar({
          open: true,
          severity: "error",
          message: e?.response?.data?.message,
        })
      );
      dispatch(setIsSubmitting(false));
      return;
    }
  }
);

export const signInUserAction = createAsyncThunk(
  "auth/signinUser",
  async ({ data, navigate }: any, { dispatch }) => {
    try {
      dispatch(setSignInFormError(""));
      dispatch(setIsSubmitting(true));

      let signIn;
      if (data.registration_type === REGISTRATION_TYPES.EMAIL) {
        // Do firebase singin to set required headers
        signIn = await authService.signInUser(data);
      }
      // Call API to get and set auth token
      const FCMToken: string | null = await prepareNotificationToken();
      const postCall: any = await authService.getAuthToken(
        data.registration_type === REGISTRATION_TYPES.EMAIL
          ? signIn?.user?.uid
          : data.firebase_id ?? "",
        FCMToken
      );
      console.log({ postCall });

      if (!postCall?.status) {
        dispatch(
          setSignInFormError("Email not exists or some error occurred.")
        );
        dispatch(setIsSubmitting(false));
        authService.doLogout();
        return;
      }

      dispatch(
        handleSnackBar({
          open: true,
          severity: "success",
          message: "You have successfully signed in!!",
        })
      );

      setAccessToken(postCall.data.data.token);

      dispatch(setIsSubmitting(false));
      dispatch(getUserProfile());
      navigate();
      return postCall.data;
    } catch (e: any) {
      if (e.code === "auth/wrong-password") {
        dispatch(setSignInFormError("Email or password is incorrect."));
        dispatch(setIsSubmitting(false));
      }
      if (e.code === "auth/user-not-found") {
        dispatch(
          setSignInFormError("User is not exists with this email address.")
        );
        dispatch(setIsSubmitting(false));
      } else {
        if (e.response.data?.data?.code === "EMAIL_NOT_VERIFIED") {
          dispatch(setOtpVerification(true));
          dispatch(setOtpTokenData(e.response.data?.data?.otp));
          setAccessToken(e.response.data?.data?.token);
        }

        dispatch(
          handleSnackBar({
            open: true,
            severity: "error",
            message: e.response.data.message,
          })
        );
        dispatch(setIsSubmitting(false));
      }
      return false;
    }
  }
);

export const logoutUserAction = createAsyncThunk(
  "auth/logoutUser",
  async ({ navigate }: any, { dispatch }) => {
    try {
      dispatch(setIsSubmitting(true));

      // Do firebase logout
      await authService.doLogout();

      // Remove token to be null
      removeAccessToken();

      dispatch(
        handleSnackBar({
          open: true,
          severity: "success",
          message: "You have successfully logged out!!",
        })
      );
      dispatch(setIsSubmitting(false));
      navigate();
      // return postCall.data
      return true;
    } catch (e: any) {
      dispatch(
        handleSnackBar({
          open: true,
          severity: "error",
          message: e.response.data.message,
        })
      );
      dispatch(setIsSubmitting(false));
    }
  }
);

export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ data, navigate }: any, { dispatch }) => {
    try {
      dispatch(setVerifyingOtp(true));
      const FCMToken = await prepareNotificationToken();
      const postCall = await userService.verifyOtp({
        body: {
          ...data,
          FCMToken,
        },
      });

      const authId = getAuthUserId();
      // Call API to get and set auth token
      const tokenRes = await authService.getAuthToken(authId, null);
      setAccessToken(tokenRes.data.data.token);

      dispatch(
        handleSnackBar({
          open: true,
          severity: "success",
          message: MESSAGES.VERIFICATION_SUCCESS,
        })
      );
      dispatch(setVerifyingOtp(false));
      dispatch(setOtpVerification(false));
      dispatch(getUserProfile());

      navigate();
      return postCall.data;
    } catch (e: any) {
      console.log(e);
      dispatch(
        handleSnackBar({
          open: true,
          severity: "error",
          message: e.response.data.message,
        })
      );
      dispatch(setVerifyingOtp(false));
    }
  }
);

export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async ({ data, navigate }: any, { dispatch }) => {
    try {
      const postCall = await userService.sendOtpForVerification({ body: data }); //postAPICall(nextAPIRoutes.OTP_VERIFICATION, data)
      dispatch(setVerifyingOtp(false));

      dispatch(
        handleSnackBar({
          open: true,
          severity: "success",
          message: "OTP sent for verification",
        })
      );

      navigate();
      return postCall.data.data;
    } catch (e: any) {
      dispatch(
        handleSnackBar({
          open: true,
          severity: "error",
          message: e.response.data.message,
        })
      );
      dispatch(setVerifyingOtp(false));
    }
  }
);

export const sendOTP = createAsyncThunk(
  "auth/sendOTP",
  async (
    {
      data,
      navigate,
      onError = null,
    }: { data: any; navigate: any; onError?: any },
    { dispatch }
  ) => {
    try {
      const postCall = await userService.sendOtpForVerification({ body: data }); //postAPICall(nextAPIRoutes.OTP_VERIFICATION, data)

      dispatch(
        handleSnackBar({
          open: true,
          severity: "success",
          message: "OTP sent for verification",
        })
      );
      dispatch(setOtpVerification(true));
      dispatch(setOtpTokenData(postCall.data?.data));
      navigate();
      return postCall.data.data;
    } catch (e: any) {
      if (onError) {
        onError();
      }
      dispatch(
        handleSnackBar({
          open: true,
          severity: "error",
          message: e.response.data.message,
        })
      );
      dispatch(setVerifyingOtp(false));
    }
  }
);

/**
 * @slice authReducers
 */
const authSlice = createSlice({
  name: "auth",
  initialState: {
    isLoggedIn: false,
    loginState: { displayName: "test", pic: "", email: "" },
    isLoadingAuth: true,
    isSubmitting: false,
    otpVerification: false,
    verifyingOtp: false,
    otpTokenData: null as object | null,
    showMobileOtpDialog: false,
    defaultPhoneForVerification: "",

    signInFormError: "" as string,
    signUpFormError: "" as string,

    unReadMessages: [] as Array<any>,
  },
  reducers: {
    setIsSubmitting: (state, { payload }: { payload: boolean }) => {
      state.isSubmitting = payload;
    },
    setOtpVerification: (state, { payload }: { payload: boolean }) => {
      state.otpVerification = payload;
    },
    setVerifyingOtp: (state, { payload }: { payload: boolean }) => {
      state.verifyingOtp = payload;
    },
    setOtpTokenData: (state, { payload }: { payload: object }) => {
      state.otpTokenData = payload;
    },
    setShowMobileOtpDialog: (state, { payload }: { payload: boolean }) => {
      state.showMobileOtpDialog = payload;
    },
    setDefaultPhoneForVerification: (state, { payload }: { payload: any }) => {
      state.defaultPhoneForVerification = payload;
    },
    setSignInFormError: (state, { payload }: { payload: string }) => {
      state.signInFormError = payload;
    },
    setSignUpFormError: (state, { payload }: { payload: string }) => {
      state.signUpFormError = payload;
    },
    setUnReadMessages: (state, { payload }: { payload: Array<any> }) => {
      state.unReadMessages = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setIsLoggedIn.fulfilled, (state, { payload }: any) => {
        state.isLoggedIn = payload;
        if (!payload) {
          state.loginState = { displayName: "test", pic: "", email: "" };
        }
      })
      .addCase(setIsLoginData.fulfilled, (state, { payload }: any) => {
        state.loginState = payload;
      })
      .addCase(setIsLoadingAuth.fulfilled, (state, { payload }: any) => {
        state.isLoadingAuth = payload;
      })
      .addCase(resendOtp.fulfilled, (state, { payload }: any) => {
        state.otpTokenData = payload;
      });
  },
});

export const {
  setIsSubmitting,
  setOtpVerification,
  setOtpTokenData,
  setVerifyingOtp,
  setShowMobileOtpDialog,
  setDefaultPhoneForVerification,
  setSignInFormError,
  setSignUpFormError,
  setUnReadMessages,
} = authSlice.actions;
export default authSlice.reducer;
